/**
 * Scoreboard Store
 *
 * Aggregates session results across all sessions in the workspace and delivers
 * live updates as scores roll in. Three real-time channels are maintained:
 *
 *   1. session_results INSERT/UPDATE — new ranking rows from completed/updating sessions
 *   2. session_events  INSERT        — collaboration events for the activity feed
 *   3. tournaments     UPDATE        — tournament state changes
 *
 * Rankings are re-derived any time `results` change. Each item gets a
 * normalized cross-session score (avg of per-session relative-rank %) so items
 * that consistently finish near the top float to rank #1.
 *
 * Fallback: 30-second polling when Realtime is unavailable.
 *
 * Source: DOCS_V4/00-canon/ — scoreboard is a read-only view over
 *   session_results + prioritization_sessions + tournaments.
 */

import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../lib/supabaseSync';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface SessionResult {
  id: string;
  sessionId: string;
  trackId: string;
  itemId: string;
  rank: number;
  score: number | null;
  createdAt: Date;
  workspaceId: string | null;
}

export interface ScoreboardSession {
  id: string;
  name: string;
  state: string;
  participantIds: string[];
  itemIds: string[];
  updatedAt: Date;
}

export interface ActivityEvent {
  id: string;
  sessionId: string;
  actorId: string;
  actorName: string;
  eventType: string;
  payload: Record<string, unknown>;
  createdAt: Date;
}

/** Derived, always sorted rank 1…N (1 = highest cross-session score). */
export interface RankedItem {
  rank: number;
  /** Rank before the latest re-computation; null on first load. */
  prevRank: number | null;
  /** Positive = climbed (better); negative = fell; 0 = unchanged. */
  rankDelta: number | null;
  itemId: string;
  /** 0–100: average of per-session relative-rank scores. */
  crossScore: number;
  sessionCount: number;
  /** Session IDs this item appears in. */
  sessionIds: string[];
  lastUpdated: Date;
}

/** Summary of a completed session — shown as a "game result" card. */
export interface CompletedSessionSummary {
  id: string;
  name: string;
  completedAt: Date;
  participantCount: number;
  itemCount: number;
  /** Top 5 items in final rank order. */
  topItems: Array<{ rank: number; itemId: string; score: number | null }>;
  trackId?: string;
  cycleId?: string;
}

/** Tournament round summary for the scoreboard. */
export interface ScoreboardTournamentRound {
  id: string;
  roundNumber: number;
  label?: string;
  state: string;
  advancingItemIds?: string[];
  eliminatedItemIds?: string[];
  sessionIds: string[];
  completedAt?: Date;
}

/** Tournament snapshot for the scoreboard. */
export interface ScoreboardTournament {
  id: string;
  name: string;
  description?: string;
  state: string;
  rounds: ScoreboardTournamentRound[];
  finalRankedItemIds?: string[];
  facilitatorId?: string;
  workspaceId?: string;
  createdAt: Date;
  completedAt?: Date;
  itemIds: string[];
}

/** Hall-of-fame entry: an item that consistently tops the charts. */
export interface HallOfFameEntry {
  itemId: string;
  totalSessions: number;
  /** How many times this item ranked #1. */
  goldCount: number;
  /** How many times this item ranked #2. */
  silverCount: number;
  /** How many times this item ranked #3. */
  bronzeCount: number;
  /** Average rank across all sessions (lower = better). */
  avgRank: number;
  /** Cross-session score (0–100). */
  crossScore: number;
  lastSession?: Date;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function rowToResult(row: Record<string, unknown>): SessionResult {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    trackId: row.track_id as string,
    itemId: row.item_id as string,
    rank: row.rank as number,
    score: row.score != null ? (row.score as number) : null,
    createdAt: row.created_at != null ? new Date(row.created_at as string) : new Date(0),
    workspaceId: (row.workspace_id as string) ?? null,
  };
}

function rowToSession(row: Record<string, unknown>): ScoreboardSession {
  return {
    id: row.id as string,
    name: (row.name as string) || 'Unnamed session',
    state: (row.state as string) || 'DRAFT',
    participantIds: Array.isArray(row.participant_ids) ? (row.participant_ids as string[]) : [],
    itemIds: Array.isArray(row.item_ids) ? (row.item_ids as string[]) : [],
    updatedAt: row.updated_at != null ? new Date(row.updated_at as string) : new Date(0),
  };
}

function rowToEvent(row: Record<string, unknown>): ActivityEvent {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    actorId: row.actor_id as string,
    actorName: (row.actor_name as string) || 'Someone',
    eventType: row.event_type as string,
    payload: (row.payload as Record<string, unknown>) || {},
    createdAt: row.created_at != null ? new Date(row.created_at as string) : new Date(0),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToTournament(row: Record<string, any>): ScoreboardTournament {
  return {
    id: row.id as string,
    name: (row.name as string) || 'Unnamed tournament',
    description: row.description ?? undefined,
    state: (row.state as string) || 'DRAFT',
    rounds: [],
    finalRankedItemIds: Array.isArray(row.final_ranked_item_ids) ? row.final_ranked_item_ids : undefined,
    facilitatorId: row.facilitator_id ?? undefined,
    workspaceId: row.workspace_id ?? undefined,
    createdAt: row.created_at != null ? new Date(row.created_at as string) : new Date(0),
    completedAt: row.completed_at != null ? new Date(row.completed_at as string) : undefined,
    itemIds: Array.isArray(row.item_ids) ? (row.item_ids as string[]) : [],
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToTournamentRound(row: Record<string, any>): ScoreboardTournamentRound {
  return {
    id: row.id as string,
    roundNumber: row.round_number as number,
    label: row.label ?? undefined,
    state: (row.state as string) || 'PENDING',
    advancingItemIds: Array.isArray(row.advancing_item_ids) ? row.advancing_item_ids : undefined,
    eliminatedItemIds: Array.isArray(row.eliminated_item_ids) ? row.eliminated_item_ids : undefined,
    sessionIds: Array.isArray(row.session_ids) ? (row.session_ids as string[]) : [],
    completedAt: row.completed_at != null ? new Date(row.completed_at as string) : undefined,
  };
}

/**
 * Compute cross-session rankings.
 *
 * For each item, determine how well it ranked in each session relative to
 * the total items in that session:
 *   relativeScore = (totalItems - rank + 1) / totalItems * 100
 * Then average across sessions.
 */
function computeRankings(
  results: SessionResult[],
  prevRanked: RankedItem[],
): RankedItem[] {
  // Build per-session item count map
  const sessionItemCounts = new Map<string, number>();
  for (const r of results) {
    const existing = sessionItemCounts.get(r.sessionId) ?? 0;
    if (r.rank > existing) sessionItemCounts.set(r.sessionId, r.rank);
  }

  // Aggregate per item
  const byItem = new Map<string, { scores: number[]; sessions: string[]; lastUpdated: Date }>();
  for (const r of results) {
    const totalItems = sessionItemCounts.get(r.sessionId) ?? r.rank;
    const relative = totalItems > 1
      ? ((totalItems - r.rank + 1) / totalItems) * 100
      : 100;

    if (!byItem.has(r.itemId)) {
      byItem.set(r.itemId, { scores: [], sessions: [], lastUpdated: r.createdAt });
    }
    const entry = byItem.get(r.itemId)!;
    entry.scores.push(relative);
    if (!entry.sessions.includes(r.sessionId)) entry.sessions.push(r.sessionId);
    if (r.createdAt > entry.lastUpdated) entry.lastUpdated = r.createdAt;
  }

  // Build prev rank lookup
  const prevRankMap = new Map(prevRanked.map((r) => [r.itemId, r.rank]));

  // Sort by average cross-session score descending
  const sorted = [...byItem.entries()].sort(([, a], [, b]) => {
    const avgA = a.scores.reduce((s, v) => s + v, 0) / a.scores.length;
    const avgB = b.scores.reduce((s, v) => s + v, 0) / b.scores.length;
    return avgB - avgA;
  });

  return sorted.map(([itemId, entry], idx) => {
    const rank = idx + 1;
    const prev = prevRankMap.get(itemId) ?? null;
    const avg = Math.round(entry.scores.reduce((s, v) => s + v, 0) / entry.scores.length);
    return {
      rank,
      prevRank: prev,
      rankDelta: prev != null ? prev - rank : null,
      itemId,
      crossScore: avg,
      sessionCount: entry.sessions.length,
      sessionIds: entry.sessions,
      lastUpdated: entry.lastUpdated,
    };
  });
}

/**
 * Derive Hall of Fame entries from raw session results.
 * Items are sorted by: gold count desc, then silver, then bronze, then avgRank asc.
 */
function computeHallOfFame(results: SessionResult[], rankings: RankedItem[]): HallOfFameEntry[] {
  const byItem = new Map<string, { ranks: number[]; sessions: string[]; lastDate: Date }>();

  for (const r of results) {
    if (!byItem.has(r.itemId)) {
      byItem.set(r.itemId, { ranks: [], sessions: [], lastDate: r.createdAt });
    }
    const entry = byItem.get(r.itemId)!;
    entry.ranks.push(r.rank);
    if (!entry.sessions.includes(r.sessionId)) entry.sessions.push(r.sessionId);
    if (r.createdAt > entry.lastDate) entry.lastDate = r.createdAt;
  }

  const crossScoreMap = new Map(rankings.map((r) => [r.itemId, r.crossScore]));

  const entries: HallOfFameEntry[] = [];
  for (const [itemId, { ranks, sessions, lastDate }] of byItem.entries()) {
    const goldCount = ranks.filter((r) => r === 1).length;
    const silverCount = ranks.filter((r) => r === 2).length;
    const bronzeCount = ranks.filter((r) => r === 3).length;
    const avgRank = ranks.reduce((a, b) => a + b, 0) / ranks.length;
    entries.push({
      itemId,
      totalSessions: sessions.length,
      goldCount,
      silverCount,
      bronzeCount,
      avgRank,
      crossScore: crossScoreMap.get(itemId) ?? 0,
      lastSession: lastDate,
    });
  }

  // Sort: most golds first, then silvers, then bronzes, then best avg rank
  entries.sort((a, b) => {
    if (b.goldCount !== a.goldCount) return b.goldCount - a.goldCount;
    if (b.silverCount !== a.silverCount) return b.silverCount - a.silverCount;
    if (b.bronzeCount !== a.bronzeCount) return b.bronzeCount - a.bronzeCount;
    return a.avgRank - b.avgRank;
  });

  return entries;
}

/**
 * Build completed session summaries from results + session data.
 * Only includes sessions in PUBLISHED or RUN_COMPLETE state.
 */
function buildCompletedSessions(
  results: SessionResult[],
  sessions: Map<string, ScoreboardSession>,
): CompletedSessionSummary[] {
  const completedStates = new Set(['PUBLISHED', 'RUN_COMPLETE']);
  const completedSessions: CompletedSessionSummary[] = [];

  for (const [sessionId, session] of sessions.entries()) {
    if (!completedStates.has(session.state)) continue;

    const sessionResults = results
      .filter((r) => r.sessionId === sessionId)
      .sort((a, b) => a.rank - b.rank);

    if (sessionResults.length === 0) continue;

    completedSessions.push({
      id: sessionId,
      name: session.name,
      completedAt: session.updatedAt,
      participantCount: session.participantIds.length,
      itemCount: sessionResults.length,
      topItems: sessionResults.slice(0, 5).map((r) => ({
        rank: r.rank,
        itemId: r.itemId,
        score: r.score,
      })),
    });
  }

  // Newest first
  return completedSessions.sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime());
}

// ─── Store ────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;
const ACTIVITY_FEED_MAX = 60;
const RESULTS_TABLE = 'session_results';
const EVENTS_TABLE = 'session_events';
const SESSIONS_TABLE = 'prioritization_sessions';
const TOURNAMENTS_TABLE = 'tournaments';
const TOURNAMENT_ROUNDS_TABLE = 'tournament_rounds';
const TOOL_SESSIONS_TABLE = 'tool_sessions';

const SESSION_SELECT = 'id, name, state, participant_ids, item_ids, updated_at';

interface ScoreboardSnapshot {
  results: SessionResult[];
  sessions: Map<string, ScoreboardSession>;
  activityFeed: ActivityEvent[];
  rankedItems: RankedItem[];
  activeCollectingSessionIds: Set<string>;
}

async function fetchScoreboardSnapshot(
  workspaceId: string | null,
  prevRanked: RankedItem[],
): Promise<ScoreboardSnapshot | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  let resultsQuery = supabase.from(RESULTS_TABLE).select('*').order('created_at', { ascending: false });
  if (workspaceId) resultsQuery = resultsQuery.eq('workspace_id', workspaceId);

  const { data: resultsData, error: resultsError } = await resultsQuery;
  if (resultsError && !resultsError.message?.includes('does not exist')) {
    console.warn('[scoreboard] results fetch error:', resultsError.message);
  }
  const results = (resultsData ?? []).map((r) => rowToResult(r as Record<string, unknown>));

  const sessionIdsFromResults = [...new Set(results.map((r) => r.sessionId))];
  const sessionsMap = new Map<string, ScoreboardSession>();
  const activeCollectingSessionIds = new Set<string>();

  // Active live tool sessions (collecting phase) — includes sessions with no results yet.
  let toolQuery = supabase
    .from(TOOL_SESSIONS_TABLE)
    .select('session_id')
    .eq('phase', 'collecting')
    .not('session_id', 'is', null);
  if (workspaceId) toolQuery = toolQuery.eq('workspace_id', workspaceId);

  const { data: collectingRows } = await toolQuery;
  for (const row of collectingRows ?? []) {
    const sid = (row as { session_id: string | null }).session_id;
    if (sid) activeCollectingSessionIds.add(sid);
  }

  const sessionIdsToFetch = [
    ...new Set([...sessionIdsFromResults, ...activeCollectingSessionIds]),
  ];

  if (sessionIdsToFetch.length > 0) {
    let sessQuery = supabase
      .from(SESSIONS_TABLE)
      .select(SESSION_SELECT)
      .in('id', sessionIdsToFetch.slice(0, 50));
    if (workspaceId) sessQuery = sessQuery.eq('workspace_id', workspaceId);

    const { data: sessData } = await sessQuery;
    for (const row of sessData ?? []) {
      const s = rowToSession(row as Record<string, unknown>);
      sessionsMap.set(s.id, s);
    }
  }

  // Sessions in CRITERIA_FINALIZED may be scoring even without a tool_session row yet.
  let liveStateQuery = supabase
    .from(SESSIONS_TABLE)
    .select(SESSION_SELECT)
    .eq('state', 'CRITERIA_FINALIZED')
    .order('updated_at', { ascending: false })
    .limit(25);
  if (workspaceId) liveStateQuery = liveStateQuery.eq('workspace_id', workspaceId);

  const { data: liveStateRows } = await liveStateQuery;
  for (const row of liveStateRows ?? []) {
    const s = rowToSession(row as Record<string, unknown>);
    sessionsMap.set(s.id, s);
  }

  let eventsQuery = supabase
    .from(EVENTS_TABLE)
    .select('*')
    .order('created_at', { ascending: false })
    .limit(ACTIVITY_FEED_MAX);
  if (workspaceId) eventsQuery = eventsQuery.eq('workspace_id', workspaceId);

  const { data: eventsData, error: eventsError } = await eventsQuery;
  if (eventsError && !eventsError.message?.includes('does not exist')) {
    console.warn('[scoreboard] events fetch error:', eventsError.message);
  }
  const activityFeed = (eventsData ?? []).map((r) => rowToEvent(r as Record<string, unknown>));

  const rankedItems = computeRankings(results, prevRanked);

  return { results, sessions: sessionsMap, activityFeed, rankedItems, activeCollectingSessionIds };
}

interface ScoreboardState {
  results: SessionResult[];
  sessions: Map<string, ScoreboardSession>;
  activityFeed: ActivityEvent[];
  rankedItems: RankedItem[];
  activeCollectingSessionIds: Set<string>;

  // New: completed session game cards
  completedSessions: CompletedSessionSummary[];

  // New: tournament data
  scoreboardTournaments: ScoreboardTournament[];

  // New: hall of fame
  hallOfFame: HallOfFameEntry[];

  isLoading: boolean;
  lastFetchedAt: Date | null;
  error: string | null;
  isRealtimeConnected: boolean;

  _resultsChannel: RealtimeChannel | null;
  _eventsChannel: RealtimeChannel | null;
  _tournamentsChannel: RealtimeChannel | null;
  _pollHandle: ReturnType<typeof setInterval> | null;

  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  teardown: () => void;

  _applyResultRow: (row: Record<string, unknown>) => void;
  _applyEventRow: (row: Record<string, unknown>) => void;
  _refreshTournaments: () => Promise<void>;
  _ensureSessionInMap: (sessionId: string) => Promise<void>;
}

export const useScoreboardStore = create<ScoreboardState>()((set, get) => ({
  results: [],
  sessions: new Map(),
  activityFeed: [],
  rankedItems: [],
  activeCollectingSessionIds: new Set(),
  completedSessions: [],
  scoreboardTournaments: [],
  hallOfFame: [],

  isLoading: false,
  lastFetchedAt: null,
  error: null,
  isRealtimeConnected: false,

  _resultsChannel: null,
  _eventsChannel: null,
  _tournamentsChannel: null,
  _pollHandle: null,

  // ─── Initialize ────────────────────────────────────────────────────────────

  initialize: async () => {
    const supabase = getSafeSupabaseClient();

    // If Supabase is unavailable at init, activate polling immediately rather
    // than waiting for a channel error that will never fire.
    if (!supabase) {
      if (!get()._pollHandle) {
        const handle = setInterval(() => get().refresh(), POLL_INTERVAL_MS);
        set({ _pollHandle: handle });
      }
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const workspaceId = getCurrentWorkspaceId();
      const wsFilter = workspaceId ? `workspace_id=eq.${workspaceId}` : undefined;

      const snapshot = await fetchScoreboardSnapshot(workspaceId, []);
      if (!snapshot) {
        set({ isLoading: false, error: 'Supabase not available' });
        return;
      }

      const completedSessions = buildCompletedSessions(snapshot.results, snapshot.sessions);
      const hallOfFame = computeHallOfFame(snapshot.results, snapshot.rankedItems);

      set({
        results: snapshot.results,
        sessions: snapshot.sessions,
        activityFeed: snapshot.activityFeed,
        rankedItems: snapshot.rankedItems,
        activeCollectingSessionIds: snapshot.activeCollectingSessionIds,
        completedSessions,
        hallOfFame,
        isLoading: false,
        lastFetchedAt: new Date(),
      });

      // ── Fetch tournaments ─────────────────────────────────────────────────
      await get()._refreshTournaments();

      // ── Subscribe to Realtime ─────────────────────────────────────────────
      get().teardown();

      const resultsChannel = supabase
        .channel('scoreboard-results')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: RESULTS_TABLE, filter: wsFilter },
          (payload) => get()._applyResultRow(payload.new as Record<string, unknown>),
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: RESULTS_TABLE, filter: wsFilter },
          (payload) => get()._applyResultRow(payload.new as Record<string, unknown>),
        )
        .subscribe((status) => {
          set({ isRealtimeConnected: status === 'SUBSCRIBED' });
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            if (!get()._pollHandle) {
              const handle = setInterval(() => get().refresh(), POLL_INTERVAL_MS);
              set({ _pollHandle: handle });
            }
          }
        });

      const eventsChannel = supabase
        .channel('scoreboard-events')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: EVENTS_TABLE, filter: wsFilter },
          (payload) => get()._applyEventRow(payload.new as Record<string, unknown>),
        )
        .subscribe();

      // Re-fetch tournaments on any tournament state change
      const tournamentsChannel = supabase
        .channel('scoreboard-tournaments')
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: TOURNAMENTS_TABLE, filter: wsFilter },
          () => get()._refreshTournaments(),
        )
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: TOURNAMENTS_TABLE, filter: wsFilter },
          () => get()._refreshTournaments(),
        )
        .subscribe();

      set({ _resultsChannel: resultsChannel, _eventsChannel: eventsChannel, _tournamentsChannel: tournamentsChannel });
    } catch (err) {
      set({ isLoading: false, error: String(err) });
    }
  },

  // ─── Refresh (polling fallback) ────────────────────────────────────────────

  refresh: async () => {
    const workspaceId = getCurrentWorkspaceId();
    const snapshot = await fetchScoreboardSnapshot(workspaceId, get().rankedItems);
    if (!snapshot) return;

    const completedSessions = buildCompletedSessions(snapshot.results, snapshot.sessions);
    const hallOfFame = computeHallOfFame(snapshot.results, snapshot.rankedItems);
    set({
      results: snapshot.results,
      sessions: snapshot.sessions,
      activityFeed: snapshot.activityFeed,
      rankedItems: snapshot.rankedItems,
      activeCollectingSessionIds: snapshot.activeCollectingSessionIds,
      completedSessions,
      hallOfFame,
      lastFetchedAt: new Date(),
    });

    await get()._refreshTournaments();
  },

  // ─── Refresh Tournaments ───────────────────────────────────────────────────

  _refreshTournaments: async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const workspaceId = getCurrentWorkspaceId();

    try {
      let tQuery = supabase
        .from(TOURNAMENTS_TABLE)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (workspaceId) tQuery = tQuery.eq('workspace_id', workspaceId);

      const { data: tData, error: tErr } = await tQuery;
      if (tErr && !tErr.message?.includes('does not exist')) {
        console.warn('[scoreboard] tournament fetch error:', tErr.message);
        return;
      }

      const tournamentList = (tData ?? []).map((r) => rowToTournament(r as Record<string, unknown>));
      const tournamentIds = tournamentList.map((t) => t.id);

      if (tournamentIds.length > 0) {
        const { data: roundsData } = await supabase
          .from(TOURNAMENT_ROUNDS_TABLE)
          .select('*')
          .in('tournament_id', tournamentIds)
          .order('round_number', { ascending: true });

        for (const row of roundsData ?? []) {
          const round = rowToTournamentRound(row as Record<string, unknown>);
          const tournament = tournamentList.find((t) => t.id === (row as Record<string, unknown>).tournament_id);
          if (tournament) tournament.rounds.push(round);
        }
      }

      set({ scoreboardTournaments: tournamentList });
    } catch (e) {
      console.warn('[scoreboard] tournament refresh error:', e);
    }
  },

  // ─── Teardown ──────────────────────────────────────────────────────────────

  teardown: () => {
    const { _resultsChannel, _eventsChannel, _tournamentsChannel, _pollHandle } = get();
    const supabase = getSafeSupabaseClient();

    if (_resultsChannel && supabase) supabase.removeChannel(_resultsChannel);
    if (_eventsChannel && supabase) supabase.removeChannel(_eventsChannel);
    if (_tournamentsChannel && supabase) supabase.removeChannel(_tournamentsChannel);
    if (_pollHandle) clearInterval(_pollHandle);

    set({
      _resultsChannel: null,
      _eventsChannel: null,
      _tournamentsChannel: null,
      _pollHandle: null,
      isRealtimeConnected: false,
    });
  },

  // ─── Real-time row handlers ────────────────────────────────────────────────

  _applyResultRow: (row) => {
    const incoming = rowToResult(row);

    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId && incoming.workspaceId && incoming.workspaceId !== workspaceId) return;

    const prev = get().results;

    const exists = prev.findIndex((r) => r.id === incoming.id);
    const next = exists >= 0
      ? prev.map((r) => (r.id === incoming.id ? incoming : r))
      : [incoming, ...prev];

    const prevRanked = get().rankedItems;
    const sessions = get().sessions;
    const rankedItems = computeRankings(next, prevRanked);
    const completedSessions = buildCompletedSessions(next, sessions);
    const hallOfFame = computeHallOfFame(next, rankedItems);

    set({ results: next, rankedItems, completedSessions, hallOfFame, lastFetchedAt: new Date() });
    void get()._ensureSessionInMap(incoming.sessionId);
  },

  _ensureSessionInMap: async (sessionId) => {
    if (get().sessions.has(sessionId)) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const workspaceId = getCurrentWorkspaceId();
    let q = supabase.from(SESSIONS_TABLE).select(SESSION_SELECT).eq('id', sessionId);
    if (workspaceId) q = q.eq('workspace_id', workspaceId);

    const { data } = await q.maybeSingle();
    if (!data) return;

    const s = rowToSession(data as Record<string, unknown>);
    const next = new Map(get().sessions);
    next.set(s.id, s);
    set({ sessions: next });
  },

  _applyEventRow: (row) => {
    const incoming = rowToEvent(row);

    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId && (row.workspace_id as string | null) && row.workspace_id !== workspaceId) return;

    const prev = get().activityFeed;
    if (prev.some((e) => e.id === incoming.id)) return;
    const next = [incoming, ...prev].slice(0, ACTIVITY_FEED_MAX);
    set({ activityFeed: next, lastFetchedAt: new Date() });
  },
}));

// ─── Selectors ────────────────────────────────────────────────────────────────

/** Sessions actively scoring (live room open or criteria finalized, not yet complete). */
export function selectLiveSessions(
  sessions: Map<string, ScoreboardSession>,
  activeCollectingSessionIds?: Set<string>,
): ScoreboardSession[] {
  const live: ScoreboardSession[] = [];
  for (const s of sessions.values()) {
    if (s.state === 'PUBLISHED' || s.state === 'RUN_COMPLETE') continue;
    if (s.state === 'CRITERIA_FINALIZED') {
      live.push(s);
      continue;
    }
    if (activeCollectingSessionIds?.has(s.id)) {
      live.push(s);
    }
  }
  return live.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}


/** Color classes for state badges. */
export function sessionStateBadgeClass(state: string): string {
  switch (state) {
    case 'CRITERIA_FINALIZED': return 'text-amber-700 bg-amber-50 border border-amber-200';
    case 'RUN_COMPLETE': return 'text-primary-700 bg-primary-50 border border-primary-200';
    case 'PUBLISHED': return 'text-green-700 bg-green-50 border border-green-200';
    default: return 'text-gray-500 bg-gray-50 border border-gray-200';
  }
}

/** Color classes for tournament state badges. */
export function tournamentStateBadgeClass(state: string): string {
  switch (state) {
    case 'DRAFT': return 'text-gray-500 bg-gray-50 border border-gray-200';
    case 'READY': return 'text-amber-700 bg-amber-50 border border-amber-200';
    case 'IN_PROGRESS': return 'text-primary-700 bg-primary-50 border border-primary-200';
    case 'AWAITING_ADVANCE': return 'text-purple-700 bg-purple-50 border border-purple-200';
    case 'COMPLETE': return 'text-green-700 bg-green-50 border border-green-200';
    case 'CANCELLED': return 'text-luminous-coral-700 bg-luminous-coral-50 border border-luminous-coral-200';
    default: return 'text-gray-500 bg-gray-50 border border-gray-200';
  }
}

/** Human-readable event type for the activity feed. */
export function eventTypeLabel(eventType: string): string {
  switch (eventType) {
    case 'score_submitted': return 'submitted scores';
    case 'item_scored': return 'scored an item';
    case 'item_moved': return 'moved an item';
    case 'item_added': return 'added an item';
    case 'item_removed': return 'removed an item';
    case 'session_phase_advanced': return 'advanced the session';
    case 'result_locked': return 'locked results';
    case 'participant_joined': return 'joined the session';
    case 'facilitator_reveal': return 'revealed results';
    default: return eventType.replace(/_/g, ' ');
  }
}
